import { supabase } from './supabase'

// ON-407: web RSVP through register_for_event.
//
// The RPC reports most outcomes as soft failures (success = false + a fixed
// English message) rather than raising, so they are classified here by
// message. Keep in step with register_for_event in running-club-backend
// (supabase/schemas/events/functions.sql).
//
// p_claim is never sent: the web cannot collect sponsorship claim choices
// (products / variants / add-ons). NULL makes the backend apply the
// sponsorship's defaults; a claim that still fails validation raises
// invalid_parameter_value, which sends the runner to the app.
//
// event_rsvp_created is emitted server-side (with rsvp_source), never here.

export type RsvpOutcome =
  | { kind: 'registered'; pickupCode: string | null; hasClaim: boolean }
  | { kind: 'waitlisted' }
  | { kind: 'already_registered' }
  | { kind: 'already_waitlisted' }
  /** Must be done in the app: selection / lottery, members-only, sponsorship claim. */
  | { kind: 'needs_app' }
  /** Deadline passed or the event has started. */
  | { kind: 'closed' }
  | { kind: 'not_found' }
  | { kind: 'signed_out' }
  | { kind: 'error' }

interface RegisterRow {
  success: boolean
  message: string | null
  registration_id: string | null
  waitlist_id: string | null
  pickup_code: string | null
  claim: unknown
}

export function classifyRegisterResult(r: Partial<RegisterRow> | null | undefined): RsvpOutcome {
  if (!r) return { kind: 'error' }
  if (r.success) {
    if (r.waitlist_id && !r.registration_id) return { kind: 'waitlisted' }
    return { kind: 'registered', pickupCode: r.pickup_code ?? null, hasClaim: r.claim != null }
  }
  const msg = (r.message ?? '').toLowerCase()
  if (msg.startsWith('already registered')) return { kind: 'already_registered' }
  if (msg.startsWith('already on waitlist')) return { kind: 'already_waitlisted' }
  if (msg.includes('not authenticated')) return { kind: 'signed_out' }
  if (msg.startsWith('event not found')) return { kind: 'not_found' }
  if (msg.includes('deadline has passed') || msg.includes('already started')) return { kind: 'closed' }
  if (msg.includes('club members') || msg.includes('registration by application')) return { kind: 'needs_app' }
  return { kind: 'error' }
}

export function classifyRegisterError(err: { code?: string; message?: string } | null | undefined): RsvpOutcome {
  // 22023 = invalid_parameter_value: sponsorship claim validation (ON-366/ON-462).
  if (err?.code === '22023') return { kind: 'needs_app' }
  return { kind: 'error' }
}

export async function registerForEventWeb(eventId: string): Promise<RsvpOutcome> {
  try {
    const { data, error } = await supabase.rpc('register_for_event', {
      p_event_id: eventId,
      p_rsvp_source: 'web',
    })
    if (error) return classifyRegisterError(error)
    const first = Array.isArray(data) ? data[0] : data
    return classifyRegisterResult(first as Partial<RegisterRow> | null)
  } catch {
    return { kind: 'error' }
  }
}
